/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
enum EventTags {
    /**
     * Special value for subscribing to all events
     */
    ALL = 'ALL',
    /**
     * @internal
     * Special event for validation of configuration and initial render.
     */
    CONFIGURE = 'CONFIGURE',
    /**
     * @internal
     * Reserved event to force refresh of app after external state change
     */
    UPDATE = 'UPDATE',
    /**
     * Start the application
     */
    START = 'START',
    /**
     * Session timeout event
     */
    TIMEOUT = 'TIMEOUT',
    /**
     * Request to update resource store for current page.
     *
     * `Event detail` contains requested change description.
     */
    RESOURCE_INFO_RECEIVED = 'RESOURCE_INFO_RECEIVED',
    /**
     * Resource loading for the page has finished successfully.
     */
    RESOURCE_LOADING_COMPLETE = 'RESOURCE_LOADING_COMPLETE',
    /**
     * Resource loading for the page has failed.
     */
    RESOURCE_LOADING_FAILED = 'RESOURCE_LOADING_FAILED',
    /**
     * New resources have been added to the current page's resource store.
     *
     * `Event.detail` contains `IResourceStore` with all new resources.
     */
    RESOURCE_STORE_CHANGED = 'RESOURCE_STORE_CHANGED',
    /* Webtop initialization events */
    WEBTOP_CONFIGURATION_COMPLETE = 'WEBTOP_CONFIGURATION_COMPLETE',
    /* Launch resource */
    LAUNCH_RESOURCE = 'LAUNCH_RESOURCE',
    /* Launch of interactive resource is complete */
    RESOURCE_LAUNCH_COMPLETE = 'RESOURCE_LAUNCH_COMPLETE',
    /* Running resource was closed */
    RESOURCE_CLOSED = 'RESOURCE_CLOSED',
    /* Generic form events */
    FORM_FIELD_CHANGED = 'FORM_FIELD_CHANGED',
    FORM_RESET = 'FORM_RESET',
    FORM_SUBMIT = 'FORM_SUBMIT',
    FORM_SUBMIT_ERROR = 'FORM_SUBMIT_ERROR',
    FORM_VALIDATE = 'FORM_VALIDATE',
    FORM_VALIDATION_ERROR = 'FORM_VALIDATION_ERROR',
    /* Generic dialog events */
    DIALOG_ACTION = 'DIALOG_ACTION',
    DIALOG_CLOSE = 'DIALOG_CLOSE',
    DIALOG_OPEN = 'DIALOG_OPEN',
    /* Endpoint Security Check events */
    EPS_CHECK_PROGRESS = 'EPS_CHECK_PROGRESS',
    EPS_CHECK_COMPLETE = 'EPS_CHECK_COMPLETE',
    EPS_CHECK_FAILED = 'EPS_CHECK_FAILED',

    /** Remaining time update for session timeout dialog */
    REMAINING_TIME_UPDATE = 'REMAINING_TIME_UPDATE',
    /* session logout */
    LOGOUT = 'LOGOUT',

    /** Okta MFA specific events */
    OKTA_MFA_EVENT = 'OKTA_MFA_EVENT',
}

/*
 * Following definitions are only for type checker and are not supposed to generate any code.
 * IEventMap has to be in the same file as enum definition.
 */
import {
    IDialogActionEventDetail,
    IDialogOpenEventDetail,
    IDialogCloseEventDetail,
} from 'apmui/components/dialog/Model';
import { FormIdentifier, IFieldChangeSpec, FormValidationContext } from 'apmui/components/form/Model';
import { IEpsCheckFailedDetail } from 'apmui/page/eps/Model';
import { IResourceMapLoaded, IResourceLoadingFailed } from './resource/ResourceLoader';
import {
    IResourceModel,
    IResourceStore,
    IResourceLaunchEventDetail,
    IResourceConfigurationEventDetail,
} from './resource';
import { IVerifyEventModel } from 'apmui/page/oktaMfa/Model';

interface IDetailMap {
    [EventTags.RESOURCE_INFO_RECEIVED]: IResourceConfigurationEventDetail;
    [EventTags.RESOURCE_LOADING_COMPLETE]: IResourceMapLoaded;
    [EventTags.RESOURCE_LOADING_FAILED]: IResourceLoadingFailed;
    [EventTags.RESOURCE_STORE_CHANGED]: IResourceStore;

    [EventTags.LAUNCH_RESOURCE]: IResourceLaunchEventDetail;
    [EventTags.RESOURCE_LAUNCH_COMPLETE]: IResourceModel;
    [EventTags.RESOURCE_CLOSED]: IResourceModel;

    [EventTags.FORM_FIELD_CHANGED]: IFieldChangeSpec;
    [EventTags.FORM_RESET]: FormIdentifier;
    [EventTags.FORM_SUBMIT]: FormIdentifier;
    [EventTags.FORM_SUBMIT_ERROR]: FormIdentifier;
    [EventTags.FORM_VALIDATE]: FormValidationContext;
    [EventTags.FORM_VALIDATION_ERROR]: FormValidationContext;

    [EventTags.DIALOG_ACTION]: IDialogActionEventDetail;
    [EventTags.DIALOG_CLOSE]: IDialogCloseEventDetail;
    [EventTags.DIALOG_OPEN]: IDialogOpenEventDetail;

    [EventTags.EPS_CHECK_PROGRESS]: string;
    [EventTags.EPS_CHECK_FAILED]: IEpsCheckFailedDetail;

    [EventTags.OKTA_MFA_EVENT]: IVerifyEventModel;
}

type IEventMap = {
    /* Events with detail type specified */
    [T in keyof IDetailMap]: IDetailMap[T] extends undefined ? Event : CustomEvent<IDetailMap[T]>;
} &
    {
        /* Events without detail */
        [T in Exclude<EventTags, keyof IDetailMap>]: Event;
    };

export { EventTags as default, EventTags, IDetailMap, IEventMap };
